import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { registerReferral, generateReferralCode } from '../services/referralService';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../styles/Login.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false); // Estado de carregamento
  const [searchParams] = useSearchParams();
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();

  const referralCode = searchParams.get('ref');

  React.useEffect(() => {
    if (referralCode) {
      setIsSignUp(true);
    }
  }, [referralCode]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const userCredential = await signInWithGoogle();
      
      if (userCredential) {
        const { uid, displayName, email } = userCredential.user;
        await initializeUserWithReferral(uid, referralCode || '', displayName, email);
      }
      
      // AuthContext irá redirecionar automaticamente
    } catch (err: any) {
      setError('Falha no login com Google. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const initializeUserWithReferral = async (userId: string, refCode: string, name?: string, email?: string) => {
    try {
      const code = generateReferralCode(userId);
      await setDoc(doc(db, 'users', userId), {
        name: name || '',
        email: email || '',
        referralCode: code,
        referralCount: 0,
        premiumReferralCount: 0,
        freeMonthsEarned: 0,
        freeMonthsUsed: 0,
        trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 dias de trial
        createdAt: new Date().toISOString(),
      }, { merge: true });

      if (refCode) {
        await registerReferral(userId, refCode);
      }
    } catch (error) {
      console.error('Erro ao processar indicação:', error);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      let userCredential;
      
      if (isSignUp) {
        userCredential = await signUpWithEmail(email, password);
        
        if (userCredential && referralCode) {
          await initializeUserWithReferral(userCredential.user.uid, referralCode);
        } else if (userCredential) {
          await initializeUserWithReferral(userCredential.user.uid, '');
        }
      } else {
        await signInWithEmail(email, password);
      }
      
      // AuthContext irá redirecionar automaticamente
    } catch (err: any) {
      setError('Falha na autenticação. Verifique seu e-mail e senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1> My Money - San Dev Code</h1>
        <p className="subtitle">Gerencie seus serviços e finanças</p>

        {referralCode && (
          <div className="referral-banner">
            Você foi indicado! Cadastre-se para começar.
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
          <span>🔐</span> {loading ? 'Aguarde...' : 'Entrar com Google'}
        </button>

        <div className="divider">
          <span>ou</span>
        </div>

        <form onSubmit={handleEmailAuth}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Aguarde...' : (isSignUp ? 'Criar Conta' : 'Entrar')}
          </button>
        </form>

        <p className="toggle-auth">
          {isSignUp ? 'Já tem conta? ' : 'Não tem conta? '}
          <span onClick={() => !loading && setIsSignUp(!isSignUp)}>
            {isSignUp ? 'Entrar' : 'Criar conta'}
          </span>
        </p>

        <div className="trial-info">
          <FontAwesomeIcon icon={faGift} /> 14 dias de teste grátis!
        </div>
      </div>
    </div>
  );
};

export default Login;
